import type { ReactNode } from 'react';
import { RotatingArtwork } from './RotatingArtwork';

/**
 * The ten hero illustrations in `apps/web/public`, in two groups.
 *
 * The first five are the hero's own, all drawn on a flat white ground — which is what lets
 * the wrapper's `mix-blend-multiply` (see `sections.tsx`) dissolve that ground into the
 * aurora behind it instead of fading the artwork's edges.
 *
 * They arrive on assorted canvases (1536x1024, 1503x1046, 1672x941), so each is normalised
 * to one frame before it lands here: artwork fitted into a 1550x800 box at (89,48) on a
 * 1672x941 canvas. That is the framing heroimg1 came with and the client approved — the
 * others originally bled to their own edges, which made them read as shifted left and
 * downward against it. Re-run that normalisation when adding to this list; matching *file*
 * dimensions is not enough, the artwork inside has to sit in the same place, and
 * `RotatingArtwork` reserves one frame for the whole set.
 *
 * The second five are the contact banner's set, shown here as well as on `/contact` — the
 * same files, not copies, so an edit to one of those illustrations reaches both places.
 * They have *not* been through the normalisation above: they are 1536x1024 with an
 * alpha-cut ground, so `object-contain` fits them to the frame's height and leaves about
 * 130px of empty space down either side, and the artwork inside does not line up with the
 * 1550x800 box the first five share. Putting them through that normalisation is what would
 * settle both. The alpha ground itself is fine under the blend: transparent and white both
 * leave the aurora untouched when multiplied.
 *
 * The list is written out rather than imported from `ContactBlock`, which is where the
 * contact section's copy lives: this is the page's largest contentful paint and it should
 * not hang the enquiry form's module graph off itself for the sake of five strings. Keep
 * the two in step by hand.
 *
 * Each PNG has a WebP sibling generated with sharp (from `apps/api`'s dependency tree —
 * this package has no image tooling of its own): ~1.5MB down to ~60KB, a ~96% saving, the
 * same treatment the About illustration gets.
 *
 * They cross-fade on a timer in a shuffled order rather than being picked once per visit —
 * see `RotatingArtwork` for why the set is held back until the page has loaded.
 */
const HERO_IMAGE_NAMES = [
  '/heroimg1',
  '/heroimg2',
  '/heroimg3',
  '/heroimg4',
  '/heroimg5',
  '/contact-banner-art',
  '/contact-banner-art2',
  '/contact-banner-art3',
  '/contact-banner-art4',
  '/contact-banner-art5',
] as const;

/** The shared canvas the first five hero illustrations are normalised onto. */
const HERO_CANVAS = { width: 1672, height: 941 } as const;

export default function HeroImage(): ReactNode {
  return (
    <RotatingArtwork
      names={HERO_IMAGE_NAMES}
      width={HERO_CANVAS.width}
      height={HERO_CANVAS.height}
      // The largest contentful paint on this page.
      priority
    />
  );
}
